import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, CheckCheck, Loader, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { notificationService } from './notificationService';
import NotificationItem from './NotificationItem';
import { btn } from '../utils/buttonStyles';

const PAGE_SIZE = 10;

const NotificationList = ({
  showMarkAllAsRead = true,
  onNotificationClick,
  // Optional: renders a close control in the header. Left out and nothing is
  // shown, so this stays usable in a page as well as a panel.
  onClose,
}) => {
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'

  const fetchPage = useCallback(async (pageOffset, append = false) => {
    if (!append) setLoading(true);
    setError(null);

    const result = await notificationService.getNotifications({
      limit: PAGE_SIZE,
      offset: pageOffset,
      unreadOnly: filter === 'unread',
    });

    if (result.success) {
      const { items = [], total: newTotal = 0 } = result.data || {};
      setTotal(newTotal);
      setNotifications(prev => append ? [...prev, ...items] : items);
    } else {
      setError(result.error);
    }

    if (!append) setLoading(false);
  }, [filter]);

  useEffect(() => {
    setOffset(0);
    fetchPage(0, false);
  }, [filter]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    const newOffset = offset + PAGE_SIZE;
    await fetchPage(newOffset, true);
    setOffset(newOffset);
    setLoadingMore(false);
  };

  const handleMarkAsRead = async (notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true, status: 'read' } : n)
    );
    notificationService.markAsRead(notificationId).catch(() => {});
    // Badge re-synced by Header's useEffect on next poll tick
  };

  const handleMarkAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true, status: 'read' })));
    notificationService.markAllAsRead().catch(() => {});
    // Badge re-synced by Header's useEffect on next poll tick
  };

  const handleNotificationClick = (notification) => {
    if (onNotificationClick) onNotificationClick(notification);
  };

  const unreadCount = notifications.filter(n => n.isRead === false || n.status === 'unread').length;
  const hasMore = notifications.length < total;

  return (
    <div className={`${theme.card} rounded-lg shadow-lg`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b ${theme.border}`}>
        <div className="flex items-center gap-x-2">
          <Bell className={`w-5 h-5 ${theme.text}`} />
          <h2 className={`text-lg font-semibold ${theme.text}`}>Notifications</h2>
          {unreadCount > 0 && (
            <span className="px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
              {unreadCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-blue-500 text-white'
                : `${theme.textSecondary} hover:${theme.backgroundSecondary}`
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              filter === 'unread'
                ? 'bg-blue-500 text-white'
                : `${theme.textSecondary} hover:${theme.backgroundSecondary}`
            }`}
          >
            Unread
          </button>

          {showMarkAllAsRead && unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className={`flex items-center gap-1.5 px-3 py-1 text-sm rounded-lg ${theme.cardHover} ${theme.textSecondary} transition-colors`}
              title="Mark all as read"
            >
              <CheckCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Mark all read</span>
            </button>
          )}

          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close notifications"
              className={`p-2 rounded-lg ${theme.cardHover} ${theme.text} transition-colors`}
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className={`${theme.textSecondary} mb-4`}>Failed to load notifications</p>
            <button
              onClick={() => fetchPage(0, false)}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
            >
              Retry
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <BellOff className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
            <p className={`${theme.textSecondary}`}>
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
                onMarkAsRead={handleMarkAsRead}
              />
            ))}
          </div>
        )}
      </div>

      {/* Load more / count footer */}
      {!loading && notifications.length > 0 && (
        <div className={`px-4 py-3 border-t ${theme.border} flex items-center justify-between`}>
          <span className={`text-xs ${theme.textMuted}`}>
            Showing {notifications.length} of {total}
          </span>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className={`flex items-center gap-2 ${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
            >
              {loadingMore ? (
                <><Loader className="w-3 h-3 animate-spin" /> Loading...</>
              ) : (
                `Load next ${Math.min(PAGE_SIZE, total - notifications.length)}`
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationList;
